import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Navigate, Route, useNavigate } from 'react-router-dom-v5-compat';

import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import { FileWithPreview } from 'dashboard/components-lib/FileUpload/types';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { ReportTemplateConfigFormData } from 'dashboard/forms/FormReportTemplate';
import { Backdrop } from 'dashboard/layouts/ShowViewLayoutV2';
import { MatchRequirementProfile } from 'dashboard/models/match-requirement-profile';
import { ReportTemplate, ReportTemplateCustomList } from 'dashboard/models/report-template';
import {
  CustomListConfigHeaderMapping,
  ReportTemplateVersion,
  ReportTemplateVersionCustomList,
} from 'dashboard/models/report-template-version';
import { CustomListConfigEditor } from 'dashboard/views/DashboardReportTemplateVersionsShowView/CustomListConfigEditor';
import { CustomListRunHistoryTable } from 'dashboard/views/DashboardReportTemplateVersionsShowView/CustomListRunHistoryTable';
import { ApiOptions } from 'lib/clients/ApiClient';
import ApiNetworkError from 'lib/errors/ApiNetworkError';
import ApiResponseError from 'lib/errors/ApiResponseError';
import usePrevious from 'lib/hooks/usePrevious';
import { ApiModel } from 'lib/interfaces/api';

interface DashboardReportTemplateVersionsShowViewProps {
  reportTemplate: ReportTemplate;
  latestPublishedVersion: ReportTemplateVersion;
  reportTemplateVersion: ReportTemplateVersion;
  versions?: ReportTemplateVersion[];
  isLoading: boolean;
  canEdit: boolean;
  saveReportTemplateError?: Error | ApiNetworkError | ApiResponseError;
  isHeaderMappingsValidated: boolean;
  validateHeaderMappingsError?: Error | ApiNetworkError | ApiResponseError;
  matchRequirementProfiles: MatchRequirementProfile[];
  handleSaveReportTemplate: (
    formValues: ReportTemplateConfigFormData,
    options?: ApiOptions,
  ) => Promise<void>;
  handleValidateHeaderMappings: (
    headerMappings: Array<CustomListConfigHeaderMapping>,
    customListFile: FileWithPreview,
  ) => Promise<void>;
  handleUpdateMatchRequirementProfiles: (
    headerMappings: Array<CustomListConfigHeaderMapping>,
  ) => Promise<void>;
  setIsHeaderMappingsValidated: (value: boolean) => void;
}

const DashboardReportTemplateVersionsShowView = ({
  reportTemplate,
  latestPublishedVersion,
  reportTemplateVersion,
  isLoading,
  canEdit,
  saveReportTemplateError,
  isHeaderMappingsValidated,
  validateHeaderMappingsError,
  handleUpdateMatchRequirementProfiles,
  matchRequirementProfiles,
  handleSaveReportTemplate,
  handleValidateHeaderMappings,
  setIsHeaderMappingsValidated,
}: DashboardReportTemplateVersionsShowViewProps) => {
  const navigate = useNavigate();
  const currentVersion = reportTemplateVersion ?? latestPublishedVersion;

  const previousLatestPublishedVersion = usePrevious(latestPublishedVersion);
  useEffect(() => {
    // if latestPublishedVersion gets updated, then assume a save happened and update the URL
    if (
      previousLatestPublishedVersion &&
      latestPublishedVersion &&
      previousLatestPublishedVersion.id !== latestPublishedVersion.id
    ) {
      navigate(`/dashboard/report-templates/${reportTemplate.id}/${latestPublishedVersion.id}`);
    }
  }, [latestPublishedVersion, history, previousLatestPublishedVersion, reportTemplate]);

  if (
    (reportTemplate == null || latestPublishedVersion == null || reportTemplateVersion == null) &&
    isLoading
  ) {
    return (
      <Backdrop>
        <LoadingIndicator fullscreen />
      </Backdrop>
    );
  } else if (
    (reportTemplate == null && !isLoading) ||
    reportTemplate.type !== ApiModel.ReportTemplateCustomList
  ) {
    return (
      <Backdrop>
        <NullState
          message='We couldn’t find that Report Template.'
          backRoute='/dashboard/report-templates'
        />
      </Backdrop>
    );
  }

  if (!reportTemplateVersion) {
    return (
      <Backdrop>
        <NullState
          message='We couldn’t find that Report Template.'
          backRoute='/dashboard/report-templates'
        />
      </Backdrop>
    );
  }

  return (
    <>
      <Helmet
        title={`${reportTemplateVersion.id} - ${reportTemplate.attributes.name} - Templates - Reports`}
      />
      <SentryRoutes>
        <Route
          index
          element={
            <CustomListConfigEditor
              reportTemplate={reportTemplate as ReportTemplateCustomList}
              version={currentVersion as ReportTemplateVersionCustomList}
              latestPublishedVersion={latestPublishedVersion}
              matchRequirementProfiles={matchRequirementProfiles}
              canEdit={canEdit}
              saveReportTemplateError={saveReportTemplateError}
              isHeaderMappingsValidated={isHeaderMappingsValidated}
              validateHeaderMappingsError={validateHeaderMappingsError}
              handleUpdateMatchRequirementProfiles={handleUpdateMatchRequirementProfiles}
              onVersionChange={(newVersionId: string) => {
                navigate(`/dashboard/report-templates/${reportTemplate.id}/${newVersionId}`);
              }}
              submit={(data) => {
                return handleSaveReportTemplate(data, { contentType: 'multipart/form-data' });
              }}
              handleValidateHeaderMappings={handleValidateHeaderMappings}
              setIsHeaderMappingsValidated={setIsHeaderMappingsValidated}
            />
          }
        />
        <Route
          path='run-history'
          element={
            <CustomListRunHistoryTable
              reportTemplate={reportTemplate as ReportTemplateCustomList}
              reportTemplateVersion={currentVersion as ReportTemplateVersionCustomList}
              historyEntries={reportTemplate.attributes['run-history']}
            />
          }
        />
        <Route path='*' element={<Navigate to='.' replace />} />
      </SentryRoutes>
    </>
  );
};

export default DashboardReportTemplateVersionsShowView;
